/**
 * buildContentViewModel - Step 2.4
 *
 * Maps domain Content to presentation-ready ContentViewModel.
 * Implements business rules for actions, buttons, and loading states.
 */

import { Content } from '../model/extras.domain';
import {
  ContentViewModel,
  PurchaseDisplayState,
  ActionItem,
  PrimaryAction,
  VideoDetails,
  AccessMethodItem,
} from './extras.viewModel.types';
import { toPurchaseDisplayState } from './extras.viewModel.mappers';
import { typeToLabel } from './extras.mappers';
import { getContentImageUrl } from './contentHelpers';
import { Dispatchers } from '@client/new/helpers/types';
import { ExtrasVMActionMap } from './extras.viewModel';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

/**
 * Input parameters for buildContentViewModel
 */
type BuildContentViewModelParams = {
  content: Content;
  isItemMutating: boolean; // From Model's mutation tracker
  isBundleCreator: boolean; // If true, hide access method menu
  isOtpOnly: boolean; // If true, disable "Sell with subscription" option
  dispatch: Dispatchers<ExtrasVMActionMap>['dispatch']; // For wiring up actions
};

/**
 * Maps Content domain model to presentation ViewModel.
 * Determines which actions are available based on business rules.
 */
export const buildContentViewModel = (
  p: BuildContentViewModelParams,
): ContentViewModel => {
  const { content, isItemMutating, isBundleCreator, isOtpOnly, dispatch } = p;
  const { purchaseInfo } = content.value;
  const contentId = content.value.id;

  // Determine publication status
  const isArchived =
    purchaseInfo.type === 'purchasable' &&
    purchaseInfo.publicationStatus === 'archived';
  const isPublished =
    purchaseInfo.type === 'purchasable' &&
    purchaseInfo.publicationStatus === 'published';

  // Determine if content has offers (bundles control visibility)
  const hasOffers =
    purchaseInfo.type === 'purchasable' &&
    purchaseInfo.accessMethod !== 'Default';

  // Determine if content is still processing (videos only)
  const isProcessing =
    content.type === 'ContentVideo' ? !content.value.isProcessed : false;

  // Business Rule: Primary Action
  let primaryAction: PrimaryAction = 'none';
  let publishButton: ContentViewModel['publishButton'] = undefined;

  if (isArchived) {
    // Archived items show "Archived" label
    primaryAction = 'archived-label';
  } else if (!hasOffers) {
    // AnyPurchase items show Publish button
    primaryAction = 'publish-button';
    publishButton = {
      label: isPublished ? 'Published' : 'Publish',
      isPublished,
      disabled: isItemMutating || isProcessing, // Disable when mutating OR processing
      loading: isItemMutating,
      onClick: () => {
        dispatch('ToggleItemVisible', contentId);
      },
    };
  }
  // else: OfferPurchase items have no primary action (bundles control visibility)

  // Business Rule: Dropdown Actions
  const dropdownActions: ActionItem[] = [];

  // Archive action - only for non-archived items
  if (!isArchived) {
    dropdownActions.push({
      id: 'archive',
      label: 'Archive',
      disabled: isItemMutating,
      onClick: () => {
        dispatch('Archive', contentId);
      },
    });
  }

  if (isArchived) {
    dropdownActions.push({
      id: 'unarchive',
      label: 'Unarchive',
      disabled: isItemMutating,
      onClick: () => {
        dispatch('Unarchive', contentId);
      },
    });
  }

  // Delete action - respects backend deletable flag
  // Business Rule: Can delete if no bundle was ever purchased
  const canDelete =
    purchaseInfo.type === 'not-purchasable' ||
    !purchaseInfo.bundles.some((b) => b.wasPurchased);
  dropdownActions.push({
    id: 'delete',
    label: 'Delete',
    disabled: !canDelete || isItemMutating,
    disabledReason: canDelete
      ? undefined
      : "Can't delete item that was purchased",
    isDestructive: true,
    onClick: () => {
      dispatch('Delete', contentId);
    },
  });

  // Get purchase display state
  const purchaseDisplay: PurchaseDisplayState = toPurchaseDisplayState({
    purchaseInfo,
  });

  // Build access methods list items for display (presentation logic moved from View)
  const accessMethodItems: AccessMethodItem[] = (() => {
    if (purchaseDisplay.type === 'not-displayed') {
      return [];
    }

    if (purchaseDisplay.type === 'default-access') {
      return [{ type: 'label', text: purchaseDisplay.label }];
    }

    const items: AccessMethodItem[] = [];

    // Add subscription label if present
    if (purchaseDisplay.type === 'subscription-and-bundles') {
      items.push({ type: 'label', text: purchaseDisplay.label });
    }

    // Add bundles
    const bundles = purchaseDisplay.bundles.map((bundle) => ({
      type: 'bundle' as const,
      id: bundle.id,
      title: bundle.title,
      status: bundle.status,
    }));

    return [...items, ...bundles];
  })();

  // Build access method menu
  const accessMethodMenu: ContentViewModel['accessMethodMenu'] = (() => {
    // Bundled creators don't see access method menu
    if (isBundleCreator) {
      return undefined;
    }

    // For "any purchase" items - show "Sell with subscription"
    if (
      purchaseDisplay.type === 'default-access' ||
      purchaseDisplay.type === 'bundles-only'
    ) {
      return {
        icon: faPlus,
        items: [
          {
            id: 'sell-as-subscription',
            label: 'Sell with subscription',
            disabled: isOtpOnly,
            disabledReason: isOtpOnly
              ? 'Set up your subscription before you can sell this product that way.'
              : undefined,
            onClick: () => {
              dispatch('SellAsSubscription', contentId);
            },
          },
        ],
      };
    }

    // For "subscription and bundles" - show "Sell as any purchase" ONLY if no bundles
    if (purchaseDisplay.type === 'subscription-and-bundles') {
      if (purchaseDisplay.bundles.length > 0) {
        return undefined;
      }

      return {
        icon: faPlus,
        items: [
          {
            id: 'sell-as-any-purchase',
            label: 'Change to any purchase',
            disabled: isItemMutating,
            onClick: () => {
              dispatch('SellAsAnyPurchase', contentId);
            },
          },
        ],
      };
    }

    // For "not-displayed" - NO menu
    return undefined;
  })();

  // Build video details if content is video
  let videoDetails: VideoDetails | undefined = undefined;
  if (content.type === 'ContentVideo') {
    videoDetails = {
      watchCount: content.value.watchCount,
      likesCount: content.value.likesCount,
      commentsCount: content.value.commentsCount,
      onCommentsClick: () => {
        dispatch('OpenComments', { contentId });
      },
    };
  }

  return {
    id: content.value.id,
    title: getContentTitle(content),
    imageUrl: getContentImageUrl(content),
    type: content.type,
    typeLabel: typeToLabel(content.type),
    publicationStatus:
      purchaseInfo.type === 'purchasable'
        ? purchaseInfo.publicationStatus
        : 'draft',
    purchaseDisplay,
    isProcessing,
    videoDetails,
    accessMethodItems,
    accessMethodMenu,
    primaryAction,
    publishButton,
    dropdownActions,
  };
};

/**
 * Get title from content (different field names per type)
 */
const getContentTitle = (content: Content): string => {
  if (content.type === 'ContentTab') {
    return content.value.name;
  }
  if ('title' in content.value) {
    return content.value.title ?? '';
  }
  return '';
};
